import { currencyDigits } from "@bt/domain";
import type { Growth, Share } from "@bt/domain/report";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { localeFor } from "@/lib/format";

/**
 * How the admin app writes figures (docs/admin-app/figures.md › Rules for every figure): counts
 * without decimals, a percentage only from a base of 20 ("3 of 7" below), and the change always
 * next to it. Times show in the browser's time zone; the figures themselves are UTC days.
 */
export function useAdminFormat() {
  const { t, i18n } = useTranslation();
  return useMemo(() => {
    const locale = localeFor(i18n.language);
    const integer = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
    const oneDecimal = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
    const compactFormat = new Intl.NumberFormat(locale, {
      notation: "compact",
      maximumFractionDigits: 1,
    });
    const percentFormat = new Intl.NumberFormat(locale, {
      style: "percent",
      maximumFractionDigits: 1,
    });
    const smallPercentFormat = new Intl.NumberFormat(locale, {
      style: "percent",
      maximumSignificantDigits: 2,
    });
    const relative = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    const dateTimeFormat = new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    });
    const signed = (n: number, text: string) => (n > 0 ? `+${text}` : text);
    const dayFormat = new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
      timeZone: "UTC",
    });
    const monthFormat = new Intl.DateTimeFormat(locale, {
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });
    const hourFormat = new Intl.DateTimeFormat(locale, {
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
    });

    const format = {
      count: (n: number | null | undefined) =>
        n === null || n === undefined ? "–" : integer.format(n),
      decimal: (n: number | null | undefined) =>
        n === null || n === undefined ? "–" : oneDecimal.format(n),
      compact: (n: number | null | undefined) =>
        n === null || n === undefined ? "–" : compactFormat.format(n),
      /** A share 0–1; under 1 % with two significant digits, so 0.02 % doesn't read as 0 %. */
      percent: (share: number | null | undefined) => {
        if (share === null || share === undefined) return "–";
        const small = share !== 0 && Math.abs(share) < 0.01;
        return (small ? smallPercentFormat : percentFormat).format(share);
      },
      /** A part of a base: a percentage from the small base on, "3 of 7" below it. */
      share: (s: Share | null | undefined) => {
        if (!s) return "–";
        return s.percent === null
          ? t("adminApp.format.partOf", {
              part: integer.format(s.part),
              base: integer.format(s.base),
            })
          : percentFormat.format(s.percent / 100);
      },
      /**
       * A change: always absolute, in the figure's own unit (`write`, a count by default), with
       * the percentage where the base allows one.
       */
      growth: (g: Growth | null | undefined, write: (n: number) => string = integer.format) => {
        if (!g) return "–";
        const change = signed(g.change, write(g.change));
        return g.percent === null
          ? change
          : `${change} (${signed(g.percent, percentFormat.format(g.percent / 100))})`;
      },
      bytes: (n: number | null | undefined) => {
        if (n === null || n === undefined) return "–";
        const units = ["B", "KB", "MB", "GB", "TB"];
        let value = n;
        let unit = 0;
        while (value >= 1000 && unit < units.length - 1) {
          value /= 1000;
          unit++;
        }
        return `${unit === 0 ? integer.format(value) : oneDecimal.format(value)} ${units[unit]}`;
      },
      ms: (n: number | null | undefined) => {
        if (n === null || n === undefined) return "–";
        return n >= 1000 ? `${oneDecimal.format(n / 1000)} s` : `${integer.format(n)} ms`;
      },
      /** A duration in seconds: its two largest units. */
      duration: (seconds: number | null | undefined) => {
        if (seconds === null || seconds === undefined) return "–";
        const days = Math.floor(seconds / 86_400);
        const hours = Math.floor((seconds % 86_400) / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        if (days > 0) return t("adminApp.format.daysHours", { days, hours });
        if (hours > 0) return t("adminApp.format.hoursMinutes", { hours, minutes });
        return t("adminApp.format.minutes", { minutes });
      },
      /** How long ago an instant was. */
      ago: (iso: string | null | undefined, now = Date.now()) => {
        if (!iso) return "–";
        const minutes = Math.round((Date.parse(iso) - now) / 60_000);
        if (Math.abs(minutes) < 60) return relative.format(minutes, "minute");
        const hours = Math.round(minutes / 60);
        if (Math.abs(hours) < 48) return relative.format(hours, "hour");
        return relative.format(Math.round(hours / 24), "day");
      },
      /**
       * A period of a history: a UTC day or a week's Monday ("23 Oct"), a month ("Oct 2026"), or
       * an hour, shown in the browser's time zone.
       */
      period: (period: string) => {
        if (/^\d{4}-\d{2}$/.test(period))
          return monthFormat.format(new Date(`${period}-01T00:00:00Z`));
        if (/^\d{4}-\d{2}-\d{2}$/.test(period))
          return dayFormat.format(new Date(`${period}T00:00:00Z`));
        return hourFormat.format(new Date(period));
      },
      dateTime: (iso: string | null | undefined) =>
        iso ? dateTimeFormat.format(new Date(iso)) : "–",
      money: (amountMinor: number | null | undefined, currency: string) => {
        if (amountMinor === null || amountMinor === undefined) return "–";
        const digits = currencyDigits(currency);
        return new Intl.NumberFormat(locale, {
          style: "currency",
          currency,
          maximumFractionDigits: 0,
        }).format(amountMinor / 10 ** digits);
      },
    };
    return format;
  }, [t, i18n.language]);
}

export type AdminFormat = ReturnType<typeof useAdminFormat>;
